import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { ComponentTypesEnum } from '../../enums/common';

import { ReportTableData } from './classes/report-table-data';
import { ReportTableColumn } from './classes/report-table-column';
import { ReportTableDataRow } from './classes/report-table-data-row';
import { ReportTableGroupedColumn } from './classes/report-table-grouped-column';

import { ReportTableConfigInterface } from './interfaces/report-table-config.interface';
import { ReportTableActionEventInterface } from './interfaces/report-table-action-event.interface';
import { ReportTableSettingsCheckboxInterface } from './interfaces/report-table-settings-checkbox.interface';
import { ReportTableDataClickEventInterface } from './interfaces/report-table-data-click-event.interface';

import { ReportTableActionsEnum } from './enums/report-table-actions.enum';
import { ReportTableButtonTypesEnum } from './enums/report-table-button-types.enum';
import { ReportTableActionMenuTypesEnum } from './enums/report-table-action-menu-types.enum';
import { ReportTableCheckboxClickEventInterface } from 'appShared/components';
import { SortColumnInterface } from 'appEntries/panel/interfaces';

@Component({
  selector: 'app-report-table',
  templateUrl: './report-table.component.html',
  styleUrls: ['./report-table.component.scss'],
})
export class ReportTableComponent implements OnInit {
  public settingsCheckboxes: ReportTableSettingsCheckboxInterface[]

  public isSettingsShown: boolean
  public componentTypes: typeof ComponentTypesEnum
  public actionsEnum: typeof ReportTableActionsEnum
  public buttonTypesEnum: typeof ReportTableButtonTypesEnum
  public actionMenuTypesEnum: typeof ReportTableActionMenuTypesEnum
  public hasGrouped: boolean
  public toggleableRowColSpan: number
  public tableColumnsLength: number

  @Input() sortedColumn: { selector: string; ascending: boolean };
  @Input() public noWrapText: boolean
  @Input() public config: ReportTableConfigInterface
  @Input() public noPaddingWrap: boolean
  @Input() public toggleForm: EventEmitter<boolean>

  @Output() public sortClicked: EventEmitter<SortColumnInterface>
  @Output() public rowClicked: EventEmitter<number | string>
  @Output() public actionButtonClicked: EventEmitter<ReportTableActionEventInterface>
  @Output() public checkboxClicked: EventEmitter<ReportTableCheckboxClickEventInterface>
  @Output() public rowCheckboxClicked: EventEmitter<ReportTableCheckboxClickEventInterface>
  @Output() public tableDataItemClicked: EventEmitter<ReportTableDataClickEventInterface>
  @Output() public toggledColumns: EventEmitter<ReportTableColumn[]>
  @Output() public headerCheckboxChecked: EventEmitter<ReportTableCheckboxClickEventInterface[]>

  constructor() {
    this.sortClicked = new EventEmitter()
    this.rowClicked = new EventEmitter()
    this.toggledColumns = new EventEmitter()
    this.actionButtonClicked = new EventEmitter()
    this.checkboxClicked = new EventEmitter()
    this.rowCheckboxClicked = new EventEmitter()
    this.tableDataItemClicked = new EventEmitter()
    this.headerCheckboxChecked = new EventEmitter()

    this.componentTypes = ComponentTypesEnum
    this.actionsEnum = ReportTableActionsEnum
    this.buttonTypesEnum = ReportTableButtonTypesEnum
    this.actionMenuTypesEnum = ReportTableActionMenuTypesEnum

    if (!this.toggleForm) {
      this.toggleForm = new EventEmitter()
    }

    this.hasGrouped = false
    this.tableColumnsLength = 0
    this.toggleableRowColSpan = 2
    this.settingsCheckboxes = []
    this.sortedColumn = { selector: null, ascending: false };
  }

  ngOnInit() {
    if (!this.config) {
      this.config = {
        rows: [],
        columns: [],
        disableColumnsOptions: true,
        hiddenColumnIds: [],
        sortByRowIndex: false,
        hasToggleableOrCheckableRows: false,
        hasHeaderCheckbox: false,
        headerChecked: false,
        form: false,
        formSettings: {
          form: null,
          arrayLength: 0,
          arraySelector: '',
          hasInlineFormIndex: false,
          inlineFormGroupIndex: 0,
        },
        actionMenu: false,
        actionMenuType: null,
        actionMenuItems: [],
      }
    }

    this.config.columns.forEach((column: ReportTableColumn | ReportTableGroupedColumn) => {
      if (column instanceof ReportTableGroupedColumn) {
        this.hasGrouped = true

        column.childColumns.forEach((childColumn: ReportTableColumn) => this.tableColumnsLength++)
      } else {
        this.tableColumnsLength++
      }

      this.settingsCheckboxes.push({
        name: column.selector,
        value: true,
        placeholder: column.label,
        disabled: false,
        colspan: column.colspan,
        shifted: column.shifted,
        sortable: column.sortable,
      })
    })

    if (this.config.sortByRowIndex) {
      this.config.rows.sort((current: ReportTableDataRow, next: ReportTableDataRow) => {
        return current.rowIndex - next.rowIndex
      })
    }

    if (this.config.actionMenuItems && Array.isArray(this.config.actionMenuItems)) {
      this.config.actionMenuItems = this.config.actionMenuItems.filter(
        (item: {
          linkText?: string
          buttonType?: ReportTableButtonTypesEnum
          allowed?: boolean
          action: ReportTableActionsEnum
        }) => {
          if (typeof item.allowed !== 'undefined') {
            return item.allowed
          }

          return true
        },
      )
    }

    if (this.config.hideHiddenColumnsOnSelection === true) {
        this.removeColumnsBySelectorList(this.config.hiddenColumnIds);
    }
  }

  onCheckboxClicked(row: ReportTableDataRow): void {
    this.config.rows.forEach((rowData) => {
      if (rowData.rowId !== row.rowId) {
        rowData.actionMenuChecked = false
      }
    })

    this.checkboxClicked.emit({
      rowId: row.rowId,
      value: row.actionMenuChecked,
    })
  }

  toggleSettings(): void {
    this.isSettingsShown = !this.isSettingsShown
  }

  toggleColumn(columnId: string, state: boolean): void {
    const shownColumns = this.settingsCheckboxes.filter(
      (checkbox: ReportTableSettingsCheckboxInterface) => checkbox.value,
    )

    if (shownColumns.length !== 1) {
      this.settingsCheckboxes.map((checkbox: ReportTableSettingsCheckboxInterface) => (checkbox.disabled = false))
    } else {
      shownColumns[0].disabled = true
    }

    if (!state) {
      if (!this.config.hiddenColumnIds.includes(columnId)) {
        this.config.hiddenColumnIds.push(columnId)
      }
    } else {
      const columnIdIndex = this.config.hiddenColumnIds.indexOf(columnId)

      if (columnIdIndex >= 0) {
        this.config.hiddenColumnIds.splice(columnIdIndex, 1)
      }
    }

    this.config.rows.map((row: ReportTableDataRow) => {
      row.dataItems.map((item: ReportTableData) => {
        if (item.colId === columnId) {
          item.hidden = !state
        }
      })
    })

    const visibleColumns = this.config.columns.filter((column: ReportTableColumn) => {
      return !this.config.hiddenColumnIds.includes(column.selector)
    })

    this.toggledColumns.emit(visibleColumns)
  }

  resetColumnState(): void {
    this.settingsCheckboxes.map((checkbox: ReportTableSettingsCheckboxInterface) => {
      checkbox.value = true
      checkbox.disabled = false
    })

    this.config.rows.map((row: ReportTableDataRow) => {
      row.dataItems.map((item: ReportTableData) => {
        item.hidden = false
      })
    })

    this.config.hiddenColumnIds = []

    this.toggledColumns.emit(this.config.columns)
  }

  removeColumnsBySelectorList(selectors: string[]): void {
    this.resetColumnState()

    if (!selectors || selectors.length === 0) {
      return
    }

    this.settingsCheckboxes.forEach((checkbox: ReportTableSettingsCheckboxInterface) => {
      if (selectors.includes(checkbox.name)) {
        checkbox.value = false
        checkbox.disabled = true
      }
    });
    selectors.forEach((selector: string) => {
        if (!this.config.hiddenColumnIds.includes(selector)) {
            this.config.hiddenColumnIds.push(...selectors);
        }
    });

    this.config.rows.forEach((row: ReportTableDataRow) => {
      row.dataItems.forEach((item: ReportTableData) => {
        if (selectors.includes(item.colId)) {
          item.hidden = false
        }
      })
    })
  }

  onRowClicked(row: ReportTableDataRow): void {
    if (!row.hasClickableDataItem) {
      this.rowClicked.emit(row.rowId)

      if (row.toggleable) {
        this.onRowToggled(row)
      }
    }
  }

  onClickAllowPropagation(event: Event, isCheckable: boolean): void {
    if (isCheckable) {
        event.stopPropagation()
    }
  }

  onHeaderChecked(e: boolean): void {
    const rows = this.config.rows
      .filter((row: ReportTableDataRow) => row.checkable)
      .map((checkedRow: ReportTableDataRow) => {
        checkedRow.checked = e
        return {
          rowId: checkedRow.rowId,
          value: checkedRow.checked,
          rowData: checkedRow,
        }
      })

    this.headerCheckboxChecked.emit(rows)
  }

  onRowChecked(checkedRow: ReportTableDataRow): void {
    if (checkedRow.isMainRow) {
      this.config.rows
        .filter((row: ReportTableDataRow) => checkedRow.rowIndex === row.subRowOf)
        .map((row: ReportTableDataRow) => (row.checked = checkedRow.checked))
    }

    this.rowCheckboxClicked.emit({
      rowId: checkedRow.rowId,
      value: checkedRow.checked,
      rowData: checkedRow,
    })
  }

  onRowToggled(toggledRow: ReportTableDataRow): void {
    if (toggledRow.isMainRow) {
      this.config.rows
        .filter((row: ReportTableDataRow) => toggledRow.rowIndex === row.subRowOf)
        .map((row: ReportTableDataRow) => (row.hidden = !row.hidden))
    }

    toggledRow.toggled = !toggledRow.toggled
  }

  sendActionCode(event: Event, actionCode: number, rowId: number | string, disabled: boolean): void {
    if (!disabled) {
      this.actionButtonClicked.emit({ actionCode, rowId })
    }

    event.stopPropagation()
  }

  onTableDataItemClick(rowId: number | string, tableDataItem: ReportTableData): void {
    if (tableDataItem.isClickable) {
      this.tableDataItemClicked.emit({
        rowId,
        colId: tableDataItem.colId,
        value: tableDataItem.value,
      })
    }
  }

  onSortClick(selector: string): void {
    if (this.sortedColumn.selector === selector) {
      this.sortedColumn.ascending = !this.sortedColumn.ascending
    } else {
      this.sortedColumn.ascending = false
      this.sortedColumn.selector = selector
    }

    this.sortClicked.emit(this.sortedColumn)
  }
}



